import {
  INSIGHT_SEVERITY_MAP,
  InsightSeverity,
  InsightType,
} from "@core/apis/PropAI/types/insights.type";

import { CheckItem } from "./CheckItemsList";

export type InsightCategory = "sensor" | "station";

export type InsightConfigBase = {
  category: InsightCategory;
  title: string;
  description: string;
  reasonIntro: string;
  checkItems: CheckItem[];
};

export type InsightConfig = InsightConfigBase & {
  type: InsightSeverity;
};

/**
 * Base config - severity is derived from INSIGHT_SEVERITY_MAP
 */
const INSIGHT_CONFIG_BASE: Record<InsightType, InsightConfigBase> = {
  too_cold_sensor: {
    category: "sensor",
    title: "cold_apartment_title",
    description: "cold_apartment_description",
    reasonIntro: "cold_apartment_reason_intro",
    checkItems: [
      { text: "cold_apartment_check_tenant" },
      { bold: "cold_apartment_check_sensor_bold", text: "cold_apartment_check_sensor_text" },
      { text: "cold_apartment_check_windows" },
      { bold: "cold_apartment_check_radiators_bold", text: "cold_apartment_check_radiators_text" },
    ],
  },
  too_warm_sensor: {
    category: "sensor",
    title: "warm_apartment_title",
    description: "warm_apartment_description",
    reasonIntro: "warm_apartment_reason_intro",
    checkItems: [
      { text: "warm_apartment_check_tenant" },
      { bold: "warm_apartment_check_sensor_bold", text: "warm_apartment_check_sensor_text" },
      { bold: "warm_apartment_check_radiators_bold", text: "warm_apartment_check_radiators_text" },
    ],
  },
  large_temperature_spread: {
    category: "station",
    title: "large_temp_spread_title",
    description: "large_temp_spread_description",
    reasonIntro: "large_temp_spread_indication",
    checkItems: [
      { text: "large_temp_spread_check_uses" },
      {
        bold: "large_temp_spread_check_radiator_sounds_bold",
        text: "large_temp_spread_check_radiator_sounds_text",
      },
      { text: "large_temp_spread_check_temp_differences" },
    ],
  },
  small_temperature_spread: {
    category: "station",
    title: "small_temp_spread_title",
    description: "small_temp_spread_description",
    reasonIntro: "small_temp_spread_upside_intro",
    checkItems: [],
  },
  optimal_temperature: {
    category: "station",
    title: "optimal_temperature_title",
    description: "optimal_temperature_description",
    reasonIntro: "optimal_temperature_upside_intro",
    checkItems: [
      { text: "optimal_temperature_benefit_comfort" },
      { text: "optimal_temperature_benefit_efficiency" },
    ],
  },
  efficient_heating: {
    category: "station",
    title: "efficient_heating_title",
    description: "efficient_heating_description",
    reasonIntro: "efficient_heating_upside_intro",
    checkItems: [
      { text: "efficient_heating_benefit_cost" },
      { text: "efficient_heating_benefit_environment" },
    ],
  },
  consistent_comfort: {
    category: "station",
    title: "consistent_comfort_title",
    description: "consistent_comfort_description",
    reasonIntro: "consistent_comfort_upside_intro",
    checkItems: [
      { text: "consistent_comfort_benefit_satisfaction" },
      { text: "consistent_comfort_benefit_stability" },
    ],
  },
  low_power_peak: {
    category: "station",
    title: "low_power_peak_title",
    description: "low_power_peak_description",
    reasonIntro: "low_power_peak_upside_intro",
    checkItems: [{ text: "low_power_peak_benefit_cost" }, { text: "low_power_peak_benefit_grid" }],
  },
  energy_efficient_building: {
    category: "station",
    title: "energy_efficient_building_title",
    description: "energy_efficient_building_description",
    reasonIntro: "energy_efficient_building_upside_intro",
    checkItems: [
      { text: "energy_efficient_building_benefit_cost" },
      { text: "energy_efficient_building_benefit_environment" },
    ],
  },
};

/**
 * Full insight config with severity derived from INSIGHT_SEVERITY_MAP
 */
export const INSIGHT_CONFIG: Record<InsightType, InsightConfig> = Object.fromEntries(
  Object.entries(INSIGHT_CONFIG_BASE).map(([key, config]) => [
    key,
    { ...config, type: INSIGHT_SEVERITY_MAP[key as InsightType] },
  ])
) as Record<InsightType, InsightConfig>;
